import svgPaths from "../../components/imports/svg-ia03w6b54n.ts";
import { eventsData, type EventData } from "../../data/events.ts";
import { useState } from "react";

type EventWithMeta = EventData & {
  sortTimestamp: number | null;
  isPastComputed: boolean;
};

function DateBox({ month, day, isHovered }: { month: string; day: string; isHovered: boolean }) {
  return (
    <div className="relative w-[75px]">
      <div className={`text-white text-center font-bold ${isHovered ? "bg-[#014a9d]" : "bg-[#003169]"} rounded-t-[5px] py-1`}>{month}</div>
      <div className={`text-2xl font-bold text-center ${isHovered ? "text-[#014a9d]" : "text-white"} border-2 ${isHovered ? "border-[#014a9d]" : "border-[#003169]"} rounded-b-[5px] py-1`}>{day}</div>
    </div>
  );
}

function LocationIcon({ isHovered }: { isHovered: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none">
      <path d={svgPaths.p28474700} fill={isHovered ? "#014a9d" : "white"} />
      <path d={svgPaths.p193f6900} fill={isHovered ? "#014a9d" : "white"} />
    </svg>
  );
}

function ArrowIcon({ isHovered }: { isHovered: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none">
      <path d={svgPaths.p35129400} stroke={isHovered ? "#014a9d" : "white"} strokeOpacity={isHovered ? 1 : 0.35} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const MONTH_INDEX: Record<string, number> = {
  jan: 0,
  january: 0,
  feb: 1,
  february: 1,
  mar: 2,
  march: 2,
  apr: 3,
  april: 3,
  may: 4,
  jun: 5,
  june: 5,
  jul: 6,
  july: 6,
  aug: 7,
  august: 7,
  sep: 8,
  sept: 8,
  september: 8,
  oct: 9,
  october: 9,
  nov: 10,
  november: 10,
  dec: 11,
  december: 11,
};

function normalizeMonthIndex(value?: string): number | null {
  if (!value) return null;
  const key = value.trim().toLowerCase().replace(".", "");
  if (!key) return null;
  return Object.prototype.hasOwnProperty.call(MONTH_INDEX, key) ? MONTH_INDEX[key] : null;
}

function buildLocalDateTimestamp(year: number, monthIndex: number, day: number): number | null {
  if (!Number.isFinite(year) || !Number.isFinite(monthIndex) || !Number.isFinite(day)) return null;
  if (monthIndex < 0 || monthIndex > 11 || day < 1 || day > 31) return null;
  const date = new Date(year, monthIndex, day);
  if (Number.isNaN(date.getTime())) return null;
  if (date.getFullYear() !== year || date.getMonth() !== monthIndex || date.getDate() !== day) return null;
  return date.getTime();
}

function parseDateString(value: string, today: Date): number | null {
  const trimmed = value.trim();
  if (!trimmed || trimmed.toLowerCase() === "tbd") return null;

  const isoMatch = trimmed.match(/^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})$/);
  if (isoMatch?.groups) {
    const year = Number(isoMatch.groups.year);
    const monthIndex = Number(isoMatch.groups.month) - 1;
    const day = Number(isoMatch.groups.day);
    return buildLocalDateTimestamp(year, monthIndex, day);
  }

  const monthDayMatch = trimmed.match(/^(?<month>[A-Za-z]+)\s+(?<day>\d{1,2})(?:,?\s*(?<year>\d{4}))?$/);
  if (monthDayMatch?.groups) {
    const monthIndex = normalizeMonthIndex(monthDayMatch.groups.month);
    if (monthIndex === null) return null;
    const day = Number(monthDayMatch.groups.day);
    const year = monthDayMatch.groups.year ? Number(monthDayMatch.groups.year) : today.getFullYear();
    return buildLocalDateTimestamp(year, monthIndex, day);
  }

  const numericMatch = trimmed.match(/^(?<month>\d{1,2})\/(?<day>\d{1,2})(?:\/(?<year>\d{4}))?$/);
  if (numericMatch?.groups) {
    const monthIndex = Number(numericMatch.groups.month) - 1;
    const day = Number(numericMatch.groups.day);
    const year = numericMatch.groups.year ? Number(numericMatch.groups.year) : today.getFullYear();
    return buildLocalDateTimestamp(year, monthIndex, day);
  }

  return null;
}

function EventCard({ month, day, title, location, url }: { month: string; day: string; title: string; location: string; url?: string }) {
  const [isHovered, setIsHovered] = useState(false);

  const handleClick = () => {
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div
      className={`${isHovered ? "bg-white" : "bg-transparent"} transition-colors rounded-lg ${url ? "cursor-pointer" : ""}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      role={url ? "button" : undefined}
      tabIndex={url ? 0 : undefined}
      onKeyDown={(e) => {
        if (url && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="flex items-center justify-between px-2 md:px-6 py-4 md:py-6">
        <div className="flex items-center gap-3 md:gap-6 flex-1">
          <DateBox month={month} day={day} isHovered={isHovered} />
          <div className="flex-1 min-w-0">
            <div className={`text-base md:text-xl font-bold ${isHovered ? "text-[#014a9d]" : "text-white"} break-words`}>{title}</div>
            <div className="flex items-start gap-2 mt-1">
              <LocationIcon isHovered={isHovered} />
              <div className={`text-xs md:text-sm ${isHovered ? "text-[#014a9d]" : "text-white"} break-words flex-1`}>{location}</div>
            </div>
          </div>
        </div>
        <div className="ml-2 flex-shrink-0">
          <ArrowIcon isHovered={isHovered} />
        </div>
      </div>
      <div className="border-t border-white/50" />
    </div>
  );
}

function parseEventDate(event: EventData, today: Date): number | null {
  const base = event.sortDate || event.date;
  if (base) {
    const parsed = parseDateString(base, today);
    if (parsed !== null) return parsed;
  }

  const monthIndex = normalizeMonthIndex(event.month);
  const day = event.day ? Number(event.day) : Number.NaN;
  if (monthIndex !== null && Number.isFinite(day)) {
    return buildLocalDateTimestamp(today.getFullYear(), monthIndex, day);
  }

  return null;
}

export default function EventsSection() {
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();

  const visibleEvents = eventsData.filter((event) => !event.isHidden);

  const eventsWithMeta: EventWithMeta[] = visibleEvents.map((event) => {
    const sortTimestamp = parseEventDate(event, today);
    // An event becomes "past" once its event date is before today.
    const isPastComputed = event.isPast ?? (sortTimestamp !== null ? sortTimestamp < startOfToday : false);

    return { ...event, sortTimestamp, isPastComputed };
  });

  const upcomingEvents = eventsWithMeta
    .filter((event) => !event.isPastComputed)
    .sort((a, b) => {
      if (a.sortTimestamp === null && b.sortTimestamp === null) return 0;
      if (a.sortTimestamp === null) return 1;
      if (b.sortTimestamp === null) return -1;
      return a.sortTimestamp - b.sortTimestamp;
    });

  const pastEvents = eventsWithMeta
    .filter((event) => event.isPastComputed)
    .sort((a, b) => {
      if (a.sortTimestamp === null && b.sortTimestamp === null) return 0;
      if (a.sortTimestamp === null) return 1;
      if (b.sortTimestamp === null) return -1;
      return b.sortTimestamp - a.sortTimestamp;
    });

  const renderSection = (title: string, events: EventWithMeta[], emptyMessage: string) => {
    const visibleEvents = events.slice(0, 3);
    return (
      <div className="mt-6">
        <div className="flex items-center justify-between pb-3 border-b border-white/40">
          <h3 className="text-lg md:text-xl font-bold">{title}</h3>
        </div>
        {visibleEvents.length ? (
          <div className="mt-2 rounded-lg border border-white/20 overflow-hidden">
            {visibleEvents.map((e) => (
              <EventCard key={e.id} month={e.month} day={e.day} title={e.title} location={e.time ? `${e.location} - ${e.time}` : e.location} url={e.url} />
            ))}
          </div>
        ) : (
          <div className="mt-3 text-sm text-white/70">{emptyMessage}</div>
        )}
      </div>
    );
  };

  return (
    <section id="events" className="bg-[#00499d] text-white py-8 md:py-[50px]">
      <div className="max-w-[1200px] mx-auto px-4">
        {renderSection("Upcoming Events", upcomingEvents, "No upcoming events right now. Check back soon.")}
        {renderSection("Past Events", pastEvents, "No past events to share yet. Check back soon for recaps.")}
      </div>
    </section>
  );
}
